<template>
  <PageChrome>
    <template #title>Email Settings</template>

    <template #actions>
      <span v-if="activeTab === 'configuration' && saving" class="autoSaveIndicator">Saving…</span>
      <span v-else-if="activeTab === 'configuration' && showSavedIndicator" class="autoSaveIndicator saved">Saved</span>
    </template>

    <!-- ── TAB NAV ─────────────────────────────────────────────── -->
    <div class="tabNav">
      <button :class="['tabBtn', activeTab === 'configuration' && 'active']" @click="navigateToTab('configuration')">Configuration</button>
      <button :class="['tabBtn', activeTab === 'sending' && 'active']" @click="navigateToTab('sending')">Sending</button>
      <button :class="['tabBtn', activeTab === 'unsubscribes' && 'active']" @click="navigateToTab('unsubscribes')">Unsubscribes</button>
      <button :class="['tabBtn', activeTab === 'bounces' && 'active']" @click="navigateToTab('bounces')">Bounces</button>
    </div>

    <!-- ══ CONFIGURATION TAB ══════════════════════════════════════ -->
    <div v-show="activeTab === 'configuration'">
      <div v-if="error" class="error">{{ error }}</div>

      <div class="card">
        <div class="lead">
          Configure how the system sends reachout emails.
        </div>

        <!-- ── PROVIDERS ─────────────────────────────────────────── -->
        <div class="section">
          <div class="section-title">Providers</div>
          <div class="hint" style="margin-top:0;">
            Email providers available to send from. Connect Gmail in your profile settings.
          </div>

          <div class="providerBadges">
            <span v-if="gmailConnectedUsers.length > 0" class="providerBadge">
              <FavIcon domain="google.com" :size="14" />
              Gmail
              <span v-if="gmailConnectedUsers.length > 1" class="providerBadgeCount">{{ gmailConnectedUsers.length }}</span>
            </span>
            <span v-if="!hasAnyProvider" class="hint" style="margin:0;">
              No providers configured yet.
            </span>
          </div>

          <!-- Sender identities -->
          <div class="card" style="margin-top:12px;">
            <div class="lead" style="margin-bottom:8px; display:flex; align-items:center; justify-content:space-between; gap:10px;">
              <span>Sender identities (From)</span>
              <button class="btn" type="button" :disabled="!hasAnyProvider" @click="openSenderModalForCreate">Add sender</button>
            </div>

            <div class="hint" style="margin-top:0;">
              Configure which From addresses agents can use, plus display name, signature, and tone examples.
            </div>

            <div v-if="form.sender_identities.length === 0" class="hint" style="margin-top:8px;">
              No sender identities configured yet. Add a provider above to get started.
            </div>

            <div v-else class="senderTableWrap">
              <table class="senderTable">
                <thead>
                  <tr>
                    <th style="width:90px;">Provider</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Team member</th>
                    <th>Status</th>
                    <th style="width:100px; text-align:right;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in form.sender_identities" :key="row.email">
                    <td>
                      <span class="providerPill">
                        <FavIcon domain="google.com" :size="11" />
                        Gmail
                      </span>
                    </td>
                    <td class="mono">{{ row.email }}</td>
                    <td>{{ row.full_name || '—' }}</td>
                    <td class="muted">{{ senderUserDisplay(row) }}</td>
                    <td>
                      <button
                        type="button"
                        class="statusPill"
                        :class="row.enabled !== false ? 'statusEnabled' : 'statusDisabled'"
                        @click="toggleSenderEnabled(idx)"
                        :title="row.enabled !== false ? 'Click to disable' : 'Click to enable'"
                      >{{ row.enabled !== false ? 'Enabled' : 'Disabled' }}</button>
                    </td>
                    <td style="text-align:right; white-space:nowrap;">
                      <button class="iconBtn" type="button" title="Edit" @click="openSenderModalForEdit(idx)">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.83H5v-.92l9.06-9.06.92.92L5.92 20.08zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
                        </svg>
                      </button>
                      <button class="iconBtn danger" type="button" title="Remove" @click="removeSenderIdentity(idx)">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M6 7h12l-1 13H7L6 7zm3-3h6l1 2H8l1-2zM9 10v7m3-7v7m3-7v7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>

        <!-- ── REQUIRE APPROVAL ──────────────────────────────────── -->
        <div class="section">
          <div class="section-title">Approval</div>
          <div class="approvalCard">
            <label style="display:flex; align-items:flex-start; gap:10px; cursor:pointer;">
              <input type="checkbox" v-model="form.require_approval" style="margin-top:2px; flex-shrink:0;" />
              <span>
                <span style="font-weight:700;">Require approval before sending (global override)</span>
                <div class="hint" style="margin:4px 0 0;">
                  When enabled, all email reachouts require human approval before sending, regardless of each agent's individual auto-send setting. When disabled, each agent decides independently — agents with "Auto-Send" on will send without approval, others will still require it.
                </div>
              </span>
            </label>
          </div>
        </div>

        <!-- ── EMAIL SCHEDULER ───────────────────────────────────── -->
        <div class="section">
          <div class="section-title">Scheduling</div>

          <SchedulingSettingsFields
            v-model:timezone-aware-sending="form.timezone_aware_sending"
            v-model:default-us-timezone="form.default_us_timezone"
            v-model:default-send-timezone="form.default_send_timezone"
          />
        </div>

        <!-- ── UNSUBSCRIBE FOOTER ─────────────────────────────────── -->
        <div class="section">
          <div class="section-title">Unsubscribe Footer</div>

          <div class="hint" style="margin-top:0;">
            A standard unsubscribe link is included in every email by default. You can configure a custom phrase below. It must include <code>{unsubscribe_url}</code> which will be substituted at runtime with the real unsubscribe link.
          </div>

          <div class="field" style="margin-top:8px;">
            <textarea
              class="textarea"
              rows="3"
              v-model="form.unsubscribe_text"
              placeholder="Unsubscribe: {unsubscribe_url}"
            />
            <div v-if="unsubscribeTextError" class="hint" style="margin:4px 0 0; color:var(--danger);">{{ unsubscribeTextError }}</div>
            <div v-else class="hint" style="margin:4px 0 0;">Leave blank to use the default.</div>
          </div>

          <div class="field" style="margin-top:8px;">
            <label style="font-size:12px; font-weight:600;">Link label</label>
            <input
              class="input"
              v-model="form.unsubscribe_link_label"
              placeholder="Unsubscribe"
              maxlength="100"
              style="max-width:300px;"
            />
            <div class="hint" style="margin:4px 0 0;">The clickable text in the unsubscribe link. Defaults to "Unsubscribe". Individual senders can override this in their signature settings.</div>
          </div>
        </div>

        <!-- ── MODE ─────────────────────────────────────────────── -->
        <div class="section">
          <div class="section-title">Mode</div>

          <div class="hint" style="margin-top:0;">
            Sending settings control whether reachouts are actually sent by the background worker.
          </div>

          <div style="border:1px solid var(--border); border-radius:12px; padding:12px; background:var(--surface);">
            <div class="field" style="margin:0;">
              <label>Mode</label>
              <select class="input" v-model="form.mode">
                <option value="development">Development</option>
                <option value="production">Production</option>
              </select>

              <div v-if="String(form.mode) === 'development'" class="hint" style="margin-top:8px;">
                In development mode, reachouts are sent to a single overridden recipient.
              </div>
            </div>

            <div v-if="String(form.mode) === 'development'" style="margin-top:10px; display:flex; flex-direction:column; gap:10px;">
              <div class="field" style="margin:0;">
                <label>Development recipient override (To)</label>
                <input class="input" type="text" v-model="form.development_to_override" placeholder="dev@example.com" />
              </div>

              <div class="field" style="margin:0;">
                <label>Slack notifications (Development channel)</label>
                <div class="hint" style="margin-top:0;">
                  Optionally send a Slack message when a reachout email is sent. Requires a configured Slack integration.
                </div>

                <div style="display:flex; align-items:center; gap:10px; margin-top:6px;">
                  <SearchSelect
                    v-if="slackChannels.length > 0"
                    v-model="form.slack_channel_development"
                    :options="slackChannelOptions"
                    placeholder="Select a channel…"
                    search-placeholder="Search channels…"
                    :allow-clear="true"
                    clear-label="(none)"
                    :max-results="200"
                    style="flex:1;"
                  />
                  <input v-else class="input" type="text" v-model="form.slack_channel_development" placeholder="C0123456789" style="flex:1;" />
                  <button class="btn" type="button" :disabled="slackChannelsLoading" @click="loadSlackChannels(true)">
                    {{ slackChannelsLoading ? 'Refreshing…' : 'Refresh channels' }}
                  </button>
                </div>

                <div v-if="slackChannelsLoading" class="hint" style="margin-top:8px;">Loading Slack channels…</div>
                <div v-else-if="slackChannelsError" class="hint" style="margin-top:8px;">{{ slackChannelsError }}</div>
                <div v-else class="hint" style="margin-top:8px;">
                  Selected: {{ slackChannelNameForId(form.slack_channel_development) }}
                </div>
              </div>
            </div>

            <div v-else style="margin-top:10px; display:flex; flex-direction:column; gap:10px;">
              <div class="field" style="margin:0;">
                <label>Slack notifications (Production channel)</label>
                <div class="hint" style="margin-top:0;">
                  Optionally send a Slack message when a reachout email is sent. Requires a configured Slack integration.
                </div>

                <div style="display:flex; align-items:center; gap:10px; margin-top:6px;">
                  <SearchSelect
                    v-if="slackChannels.length > 0"
                    v-model="form.slack_channel_production"
                    :options="slackChannelOptions"
                    placeholder="Select a channel…"
                    search-placeholder="Search channels…"
                    :allow-clear="true"
                    clear-label="(none)"
                    :max-results="200"
                    style="flex:1;"
                  />
                  <input v-else class="input" type="text" v-model="form.slack_channel_production" placeholder="C0123456789" style="flex:1;" />
                  <button class="btn" type="button" :disabled="slackChannelsLoading" @click="loadSlackChannels(true)">
                    {{ slackChannelsLoading ? 'Refreshing…' : 'Refresh channels' }}
                  </button>
                </div>

                <div v-if="slackChannelsLoading" class="hint" style="margin-top:8px;">Loading Slack channels…</div>
                <div v-else-if="slackChannelsError" class="hint" style="margin-top:8px;">{{ slackChannelsError }}</div>
                <div v-else class="hint" style="margin-top:8px;">
                  Selected: {{ slackChannelNameForId(form.slack_channel_production) }}
                </div>
              </div>

              <div class="field" style="margin:0;">
                <label>Salesforce Task writeback (Production)</label>
                <div class="hint" style="margin-top:0;">
                  Production email writeback (enable, account-creation approval, and LeadIQ verification) is
                  configured in the
                  <router-link to="/integrations">Salesforce integration → Export</router-link> tab.
                </div>
              </div>
            </div>
          </div>

          <div class="send-toggle" style="margin-top:12px;">
            <label style="margin:0;">
              <ToggleSwitch v-model="form.enabled" :disabled="!form.enabled && !canEnableSending" label="Allow sending" />
            </label>

            <div v-if="!canEnableSending" class="hint" style="margin:8px 0 0 0;">
              Complete the required sending configuration before enabling:
              <div v-for="msg in sendingEnableBlockers" :key="msg" style="margin-top:4px;">- {{ msg }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ SENDING TAB ════════════════════════════════════════════ -->
    <div v-show="activeTab === 'sending'" class="sendingTab">

      <!-- Filters row 1: status + sending approach + date + search -->
      <div class="sendingFilters">
        <SearchSelect
          v-model="sf_statusGroup"
          :options="sf_statusOptions"
          placeholder="All statuses"
          :hide-search="true"
          :allow-clear="false"
          style="min-width: 160px; flex: 1; max-width: 220px;"
          @update:modelValue="onSendingFilterChange"
        />
        <SearchSelect
          v-model="sf_sendingApproach"
          :options="sf_sendingApproachOptions"
          placeholder="All approaches"
          :hide-search="true"
          :allow-clear="false"
          style="min-width: 150px; flex: 1; max-width: 200px;"
          @update:modelValue="onSendingFilterChange"
        />
        <select v-model="sf_dateRange" class="input filterSelect" @change="onDateRangeChange">
          <option value="all">All time</option>
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>
        <input
          class="input filterSearch"
          type="text"
          v-model="sf_search"
          placeholder="Search email"
          @input="onSendingSearchInput"
        />
      </div>

      <!-- Filters row 2: account + agent + sequence SearchSelects -->
      <div class="sendingFilters" style="gap:8px;">
        <SearchSelect
          v-model="sf_accountId"
          :optionsFunction="searchAccountsById"
          :maxResults="20"
          placeholder="All accounts"
          search-placeholder="Search accounts…"
          clear-label="All accounts"
          style="min-width:180px; flex:1;"
          @update:modelValue="onSendingFilterChange"
        />
        <SearchSelect
          v-model="sf_agentId"
          :options="sf_agentOptions"
          placeholder="All agents"
          search-placeholder="Search agents…"
          :allow-clear="true"
          clear-label="All agents"
          :max-results="200"
          style="min-width:180px; flex:1;"
          @update:modelValue="onSendingFilterChange"
        />
        <SearchSelect
          v-model="sf_sequenceId"
          :options="sf_sequenceOptions"
          placeholder="All sequences"
          search-placeholder="Search sequences…"
          :allow-clear="true"
          clear-label="All sequences"
          :max-results="200"
          style="min-width:180px; flex:1;"
          @update:modelValue="onSendingFilterChange"
        />
      </div>

      <!-- Summary cards -->
      <div class="summaryCards">
        <div
          class="summaryCard"
          :class="{ active: sf_statusGroup === 'sent' }"
          @click="setSendingStatusGroup('sent')"
        >
          <div class="summaryCardValue">{{ sf_summary.sent === null ? '…' : sf_summary.sent }}</div>
          <div class="summaryCardLabel">Sent</div>
        </div>
        <div
          class="summaryCard"
          :class="{ active: sf_statusGroup === 'scheduled' }"
          @click="setSendingStatusGroup('scheduled')"
        >
          <div class="summaryCardValue">{{ sf_summary.scheduled === null ? '…' : sf_summary.scheduled }}</div>
          <div class="summaryCardLabel">Scheduled / Queued</div>
        </div>
        <div
          class="summaryCard"
          :class="{ active: sf_statusGroup === 'pending_approval' }"
          @click="setSendingStatusGroup('pending_approval')"
        >
          <div class="summaryCardValue">{{ sf_summary.pending === null ? '…' : sf_summary.pending }}</div>
          <div class="summaryCardLabel">Pending Approval</div>
        </div>
        <div
          class="summaryCard danger"
          :class="{ active: sf_statusGroup === 'failed' }"
          @click="setSendingStatusGroup('failed')"
        >
          <div class="summaryCardValue">{{ sf_summary.failed === null ? '…' : sf_summary.failed }}</div>
          <div class="summaryCardLabel">Failed</div>
        </div>
      </div>

      <!-- Daily bar chart -->
      <div class="chartSection">
        <div class="chartBars">
          <div
            v-for="day in sf_chartData"
            :key="day.date"
            class="chartBarCol"
            :class="{ active: sf_selectedDay === day.date }"
            @click="onChartBarClick(day.date)"
            :title="day.date + ' — ' + (day.sent + day.scheduled + day.pending + day.failed + day.other) + ' email' + (day.sent + day.scheduled + day.pending + day.failed + day.other === 1 ? '' : 's')"
          >
            <div class="chartBarArea">
              <div
                v-for="(seg, si) in barParts(day)"
                :key="si"
                class="chartSeg"
                :style="{ height: seg.h + 'px', background: seg.color }"
              ></div>
            </div>
            <div class="chartBarLabel">{{ chartLabel(day.date) }}</div>
          </div>
        </div>
        <div v-if="sf_selectedDay" class="chartDayFilter">
          Filtered to {{ sf_selectedDay }}
          <button class="btnLink" @click="sf_selectedDay = null; loadSendingRows(0)">clear</button>
        </div>
      </div>

      <!-- Table -->
      <div v-if="sf_loading && sf_rows.length === 0" class="hint" style="padding:20px 0;">Loading…</div>
      <div v-else-if="sf_rows.length === 0 && !sf_loading" class="hint" style="padding:20px 0;">No emails found for the current filters.</div>
      <div v-else class="emailTableWrap">
        <table class="emailTable">
          <thead>
            <tr>
              <th style="min-width:200px;">Recipient</th>
              <th style="min-width:170px;">Sender</th>
              <th style="min-width:180px;">Origin</th>
              <th style="min-width:220px;">Sending Approach</th>
              <th style="min-width:130px;">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in sf_rows" :key="row.id">

              <!-- Recipient: to + subject + dev override -->
              <td>
                <div class="cellMain mono">{{ row.to }}</div>
                <div class="cellSub cellTruncate" :title="row.subject">{{ row.subject }}</div>
                <div v-if="recipientOverride(row)" class="cellSub" style="margin-top:2px;">
                  <span class="devBadge">dev</span>
                  {{ recipientOverride(row).address }}
                  <span v-if="!recipientOverride(row).sent" class="devBadgePre">(pre-send)</span>
                </div>
              </td>

              <!-- Sender: from identity + provider -->
              <td>
                <div class="cellMain mono">{{ row.from_identity || '—' }}</div>
                <div class="cellSub" style="margin-top:3px;">
                  <span v-if="row.email_provider === 'gmail'" class="providerPill">
                    <FavIcon domain="google.com" :size="11" />
                    Gmail
                  </span>
                  <span v-else-if="row.email_provider === 'sendgrid'" class="providerPill">
                    <FavIcon domain="sendgrid.com" :size="11" />
                    SendGrid
                  </span>
                </div>
              </td>

              <!-- Origin: same badges as ReachoutModal pill-row -->
              <td>
                <div class="pill-row start" style="gap:4px;">
                  <router-link
                    v-if="row.created_by_agent_run_id"
                    :to="'/agent-runs/' + row.created_by_agent_run_id"
                    class="badge pill-link"
                    :title="row.agent_name || 'Agent run'"
                  >
                    <HeroIcon
                      v-if="row.agent_icon_name"
                      :name="row.agent_icon_name"
                      :iconStyle="row.agent_icon_style || 'outline'"
                      :color="row.agent_icon_color || 'var(--muted)'"
                      :size="14"
                    />
                    <AppIcon v-else name="agent" :size="14" />
                    <span>{{ row.agent_name || 'Agent run' }}</span>
                  </router-link>
                  <router-link
                    v-if="row.account_id && row.account_domain"
                    :to="'/accounts/' + row.account_id"
                    class="badge pill-link"
                    :title="row.account_domain"
                  >
                    <FavIcon :domain="row.account_domain" :size="14" />
                    <span>{{ row.account_domain }}</span>
                  </router-link>
                  <span
                    v-if="row.sequence_id && row.sequence_name"
                    class="badge"
                    :title="'Sequence: ' + row.sequence_name"
                  >
                    <AppIcon name="sequence" :size="14" />
                    <span>{{ row.sequence_name }}</span>
                  </span>
                </div>
              </td>

              <!-- Sending Approach: path + tz + timing -->
              <td>
                <div v-if="sendingPath(row) === 'scheduled'">
                  <span class="pathBadge scheduled">scheduled</span>
                  <div class="sendingDetail">
                    <span class="sendingDetailLabel">tz</span>
                    {{ effectiveTz(row) || '(none)' }}
                    <span v-if="effectiveTzSource(row)" class="tzSourceBadge">{{ effectiveTzSource(row) }}</span>
                  </div>
                  <div class="sendingDetail">
                    <span class="sendingDetailLabel">queued</span>
                    {{ formatDateTime(row.created_at) }}
                  </div>
                  <div v-if="row.last_send_attempt_at" class="sendingDetail">
                    <span class="sendingDetailLabel">sent</span>
                    {{ formatDateTime(row.last_send_attempt_at) }}
                  </div>
                  <div v-else-if="row.next_send_attempt_at" class="sendingDetail">
                    <span class="sendingDetailLabel">retry</span>
                    {{ formatDateTime(row.next_send_attempt_at) }}
                  </div>
                  <div v-else-if="row.status === 'scheduled_for_sending'" class="sendingDetailMuted">
                    awaiting business hours window
                    <span v-if="bizWindowEstimate(row)" style="font-style:normal;"> · {{ bizWindowEstimate(row) }}</span>
                  </div>
                </div>
                <div v-else>
                  <span class="pathBadge immediate">immediate</span>
                  <div v-if="row.last_send_attempt_at" class="sendingDetail">
                    <span class="sendingDetailLabel">sent</span>
                    {{ formatDateTime(row.last_send_attempt_at) }}
                  </div>
                </div>
              </td>

              <!-- Status: raw value only -->
              <td>
                <span class="statusPill" :style="statusPillStyle(row.status)">{{ row.status }}</span>
              </td>

            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer: count + load more -->
      <div v-if="sf_rows.length > 0" class="tableFooter">
        <span class="hint" style="margin:0;">
          Showing {{ sf_rows.length }} of {{ sf_total }} email{{ sf_total === 1 ? '' : 's' }}
        </span>
        <button
          v-if="sf_nextOffset !== null"
          class="btn"
          :disabled="sf_loading"
          @click="loadMoreSending"
        >
          {{ sf_loading ? 'Loading…' : 'Load more' }}
        </button>
      </div>
    </div>

    <!-- ══ UNSUBSCRIBES TAB ══════════════════════════════════════ -->
    <div v-show="activeTab === 'unsubscribes'">
      <UnsubscribesTab />
    </div>

    <!-- ══ BOUNCES TAB ════════════════════════════════════════════ -->
    <div v-show="activeTab === 'bounces'">
      <BouncesTab />
    </div>

    <!-- ── SENDER IDENTITY MODAL ──────────────────────────────── -->
    <Modal v-if="senderModalOpen" title="Sender identity" @close="closeSenderModal">
      <!-- Gmail: account picker -->
      <div v-if="senderDraft.provider === 'gmail' && senderModalIndex == null" class="field">
        <label>Gmail account</label>
        <div v-if="availableGmailEmailsForAdd.length === 0" class="hint" style="margin:0; padding:10px; border:1px solid var(--border); border-radius:10px;">
          All connected Gmail accounts already have sender identities. Connect additional Gmail accounts via your profile settings.
        </div>
        <select v-else class="input" v-model="senderDraft.email" @change="onGmailAccountSelected">
          <option value="">Select a Gmail account…</option>
          <option v-for="gu in availableGmailEmailsForAdd" :key="gu.user_id" :value="gu.email">
            {{ gu.display_name ? `${gu.display_name} (${gu.email})` : gu.email }}
          </option>
        </select>
      </div>

      <!-- Gmail edit: show locked email -->
      <div v-if="senderDraft.provider === 'gmail' && senderModalIndex != null" class="field">
        <label>Email</label>
        <input class="input" type="text" :value="senderDraft.email" disabled />
        <div class="hint" style="margin:0;">Gmail address is fixed to the connected account.</div>
      </div>

      <div class="field">
        <label>Team member</label>
        <SearchSelect
          v-model="senderDraft.user_id"
          :options="tenantUserOptions"
          placeholder="Select team member..."
          allow-clear
          clear-label="(none)"
        />
        <div class="hint" style="margin-top:4px;">Which team member sends from this address. Used for ownership-based routing.</div>
      </div>
      <!-- Signature + unsub managed by SenderIdentityEditor (saves independently) -->
      <SenderIdentityEditor
        v-if="senderDraft.email"
        :senderEmail="senderDraft.email"
        :identity="currentSenderIdentity"
        mode="full"
      />
      <template #footer>
        <button class="btn" type="button" @click="closeSenderModal">Cancel</button>
        <button class="btn primary" type="button" @click="onSenderModalSave">Save</button>
      </template>
    </Modal>

  </PageChrome>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import FavIcon from '../../components/FavIcon.vue'
import HeroIcon from '../../components/HeroIcon.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import ToggleSwitch from '../../components/ToggleSwitch.vue'
import { apiFetch } from '../../services/api.ts'
import Modal from '../../components/Modal.vue'
import UnsubscribesTab from '../../components/tabs/UnsubscribesTab.vue'
import BouncesTab from '../../components/tabs/BouncesTab.vue'
import SenderIdentityEditor from '../components/SenderIdentityEditor.vue'
import SchedulingSettingsFields from '../components/SchedulingSettingsFields.vue'
import { useChatScope } from '../composables/useChatScope'

const { setScope, clearScope } = useChatScope()

// ── Tab state ────────────────────────────────────────────────────
const route = useRoute()
const router = useRouter()

const VALID_TABS = ['configuration', 'sending', 'unsubscribes', 'bounces']

const activeTab = computed(() => {
  const tab = String(route.params.tab || '')
  return VALID_TABS.includes(tab) ? tab : 'configuration'
})

function navigateToTab(tab) {
  router.replace({ name: 'email-settings', params: { tab } })
}

watch(activeTab, (tab) => {
  if (tab === 'sending') {
    const firstLoad = sf_rows.value.length === 0 && !sf_loading.value
    if (firstLoad) {
      reloadSending()
      loadSendingSelects()
    }
  }
})

async function loadSendingSelects() {
  try {
    const [agentsResp, sequencesResp, statusesResp] = await Promise.all([
      apiFetch('/agents?include_sales_play_owned=true').catch(() => null),
      apiFetch('/sequences').catch(() => null),
      apiFetch('/reachouts/distinct_statuses').catch(() => null),
    ])
    sf_agents.value = Array.isArray(agentsResp?.agents) ? agentsResp.agents : []
    sf_sequences.value = Array.isArray(sequencesResp?.sequences) ? sequencesResp.sequences : []
    sf_distinctStatuses.value = Array.isArray(statusesResp?.statuses) ? statusesResp.statuses : []
  } catch {
    // non-fatal
  }
}

// ══ CONFIGURATION TAB STATE ══════════════════════════════════════

const error = ref('')
const saving = ref(false)
const showSavedIndicator = ref(false)
let _savedTimer = null

const baselineSnapshot = ref('')

const configuredProviders = ref({})
const gmailConnectedUsers = ref([])
const tenantUsers = ref([])

const tenantUserOptions = computed(() =>
  (tenantUsers.value || []).map((u) => ({
    value: String(u.id),
    label: `${String(u.first_name || '').trim()} ${String(u.last_name || '').trim()}`.trim() || u.email,
    secondaryLabel: u.email,
  }))
)

const slackChannels = ref([])
const slackChannelsLoading = ref(false)
const slackChannelsError = ref('')

const slackChannelOptions = computed(() => {
  const rows = Array.isArray(slackChannels.value) ? slackChannels.value : []
  return rows
    .map((c) => {
      const id = String(c?.id || '').trim()
      const name = String(c?.name || '').trim()
      const isPrivate = Boolean(c?.is_private)
      if (!id || !name) return null
      return {
        value: id,
        label: isPrivate ? `🔒 # ${name}` : `# ${name}`,
      }
    })
    .filter((x) => x && x.value && x.label)
})

function slackChannelNameForId(id) {
  const target = String(id || '').trim()
  if (!target) return '(none)'
  const rows = Array.isArray(slackChannels.value) ? slackChannels.value : []
  const found = rows.find((c) => String(c?.id || '') === target)
  if (!found) return target
  const name = String(found?.name || '').trim() || target
  const isPrivate = Boolean(found?.is_private)
  return isPrivate ? `🔒 # ${name}` : `# ${name}`
}

async function loadSlackChannels(forceRefresh = false) {
  slackChannelsLoading.value = true
  slackChannelsError.value = ''
  try {
    const url = forceRefresh ? '/settings/integrations/slack/channels?refresh=1' : '/settings/integrations/slack/channels'
    const resp = await apiFetch(url)
    const rows = Array.isArray(resp?.channels) ? resp.channels : []
    slackChannels.value = rows
      .map((c) => {
        const o = c && typeof c === 'object' ? c : {}
        return {
          id: String(o.id || '').trim(),
          name: String(o.name || '').trim(),
          is_private: Boolean(o.is_private),
        }
      })
      .filter((c) => c.id && c.name)
  } catch (e) {
    slackChannels.value = []
    slackChannelsError.value = e?.message || 'Failed to load Slack channels'
  } finally {
    slackChannelsLoading.value = false
  }
}

const hasAnyProvider = computed(() => {
  return (gmailConnectedUsers.value || []).length > 0
})

const availableGmailEmailsForAdd = computed(() => {
  const alreadyAdded = new Set(
    (form.sender_identities || [])
      .filter((si) => si.provider === 'gmail')
      .map((si) => String(si.email || '').toLowerCase()),
  )
  return (gmailConnectedUsers.value || []).filter(
    (gu) => !alreadyAdded.has(String(gu.email || '').toLowerCase()),
  )
})

function _normalizedSnapshotFromForm() {
  const ids = Array.isArray(form.sender_identities) ? form.sender_identities : []
  const sender_identities = ids
    .map((r) => {
      const o = r && typeof r === 'object' ? r : {}
      return {
        email: String(o.email || '').trim(),
        full_name: String(o.full_name || '').trim(),
        enabled: o.enabled !== false,
        provider: String(o.provider || 'gmail'),
        user_id: o.user_id ? String(o.user_id) : null,
        slack_member_id: o.slack_member_id ? String(o.slack_member_id).trim() || null : null,
      }
    })
    .filter((r) => r.email)

  return {
    enabled: Boolean(form.enabled),
    mode: String(form.mode || 'development'),
    provider: String(form.provider || ''),
    require_approval: Boolean(form.require_approval),
    sender_identities,
    unsubscribe_text: String(form.unsubscribe_text || ''),
    unsubscribe_link_label: String(form.unsubscribe_link_label || ''),
    development_to_override: String(form.development_to_override || ''),
    slack_channel_production: String(form.slack_channel_production || ''),
    slack_channel_development: String(form.slack_channel_development || ''),
    sf_writeback_production: {
      enabled: Boolean(form.sf_writeback_production?.enabled),
      mode: form.sf_writeback_production?.enabled ? 'enabled' : 'disabled',
      require_account_create_approval: Boolean(form.sf_writeback_production?.require_account_create_approval),
      require_leadiq_verification_for_account_create: form.sf_writeback_production?.require_leadiq_verification_for_account_create == null
        ? true
        : Boolean(form.sf_writeback_production.require_leadiq_verification_for_account_create),
    },
    default_us_timezone: String(form.default_us_timezone || ''),
    default_send_timezone: String(form.default_send_timezone || ''),
    timezone_aware_sending: Boolean(form.timezone_aware_sending),
  }
}

const form = reactive({
  enabled: false,
  mode: 'development',
  provider: 'gmail',
  require_approval: true,
  sender_identities: [],
  unsubscribe_text: '',
  unsubscribe_link_label: '',
  development_to_override: '',
  slack_channel_production: '',
  slack_channel_development: '',
  sf_writeback_production: {
    enabled: false,
    require_account_create_approval: false,
    require_leadiq_verification_for_account_create: true,
  },
  default_us_timezone: '',
  default_send_timezone: '',
  timezone_aware_sending: false,
})

const senderModalOpen = ref(false)
const senderModalIndex = ref(null)
const senderDraft = reactive({
  email: '',
  full_name: '',
  signature_html: '',
  description: '',
  sample_emails: [],
  provider: 'gmail',
  user_id: null,
  slack_member_id: null,
})

const sendingEnableBlockers = computed(() => {
  const blockers = []

  if (!Array.isArray(form.sender_identities) || form.sender_identities.length === 0) {
    blockers.push('Add at least one sender identity.')
  }

  const mode = String(form.mode || '').trim().toLowerCase()
  if (mode === 'development') {
    const to = String(form.development_to_override || '').trim()
    if (!to) blockers.push('Development mode requires a Development recipient override (To).')
    else if (!to.includes('@')) blockers.push('Development recipient override must be a valid email address.')
  }

  return blockers
})

const canEnableSending = computed(() => sendingEnableBlockers.value.length === 0)

const unsubscribeTextError = computed(() => {
  const t = String(form.unsubscribe_text || '').trim()
  if (t && !t.includes('{unsubscribe_url}')) return 'Must include {unsubscribe_url}.'
  return ''
})

function senderUserDisplay(row) {
  if (!row?.user_id) return '—'
  const u = (tenantUsers.value || []).find((x) => String(x.id) === String(row.user_id))
  if (!u) return '—'
  const name = `${String(u.first_name || '').trim()} ${String(u.last_name || '').trim()}`.trim()
  return name || u.email || '—'
}

function autoDefaultUserId(email) {
  if (!email) return null
  const match = (tenantUsers.value || []).find(
    (u) => String(u.email || '').toLowerCase() === String(email).toLowerCase()
  )
  return match ? String(match.id) : null
}

function openSenderModalForCreate() {
  senderModalIndex.value = null
  senderDraft.email = ''
  senderDraft.full_name = ''
  senderDraft.signature_html = ''
  senderDraft.description = ''
  senderDraft.sample_emails = []
  senderDraft.provider = 'gmail'
  senderDraft.user_id = null
  senderDraft.slack_member_id = null
  senderModalOpen.value = true
}

function openSenderModalForEdit(idx) {
  const i = Number(idx)
  if (!Number.isFinite(i)) return
  const row = form.sender_identities?.[i]
  if (!row || typeof row !== 'object') return
  senderModalIndex.value = i
  senderDraft.email = String(row.email || '')
  senderDraft.full_name = String(row.full_name || '')
  senderDraft.signature_html = String(row.signature_html || '')
  senderDraft.description = String(row.description || '')
  senderDraft.sample_emails = Array.isArray(row.sample_emails) ? [...row.sample_emails] : []
  senderDraft.provider = String(row.provider || 'gmail')
  senderDraft.user_id = row.user_id ? String(row.user_id) : autoDefaultUserId(row.email)
  senderDraft.slack_member_id = row.slack_member_id ? String(row.slack_member_id) : null
  senderModalOpen.value = true
}

function closeSenderModal() {
  senderModalOpen.value = false
  senderModalIndex.value = null
}

function onGmailAccountSelected() {
  const gu = (gmailConnectedUsers.value || []).find(
    (u) => u.email === senderDraft.email,
  )
  if (gu) {
    senderDraft.user_id = gu.user_id
    if (!senderDraft.full_name && gu.display_name) {
      senderDraft.full_name = gu.display_name
    }
  }
}

function saveSenderModal() {
  const provider = String(senderDraft.provider || 'gmail')
  const email = String(senderDraft.email || '').trim()

  if (!email || !email.includes('@')) throw new Error('Sender email is required')

  if (provider === 'gmail') {
    const gu = (gmailConnectedUsers.value || []).find(
      (u) => String(u.email || '').toLowerCase() === email.toLowerCase(),
    )
    if (!gu && senderModalIndex.value == null) {
      throw new Error('Selected Gmail account is not connected')
    }
  }

  const existing = new Set(
    (form.sender_identities || []).map((x, j) =>
      j === senderModalIndex.value ? '' : String(x?.email || '').toLowerCase(),
    ),
  )
  if (existing.has(email.toLowerCase())) throw new Error('Sender email already exists')

  const row = {
    email,
    full_name: String(senderDraft.full_name || '').trim(),
    signature_html: String(senderDraft.signature_html || '').trim(),
    description: String(senderDraft.description || '').trim(),
    sample_emails: Array.isArray(senderDraft.sample_emails)
      ? senderDraft.sample_emails.map((x) => String(x || '')).filter((x) => x.trim())
      : [],
    provider,
    user_id: senderDraft.user_id || null,
    slack_member_id: senderDraft.slack_member_id ? String(senderDraft.slack_member_id).trim() || null : null,
  }

  if (senderModalIndex.value == null) {
    form.sender_identities.push(row)
  } else {
    form.sender_identities.splice(Number(senderModalIndex.value), 1, row)
  }

  closeSenderModal()
}

function addSenderSampleEmail() {
  if (!Array.isArray(senderDraft.sample_emails)) senderDraft.sample_emails = []
  senderDraft.sample_emails.push('')
}

function onSenderModalSave() {
  error.value = ''
  try {
    saveSenderModal()
  } catch (e) {
    error.value = e?.message || 'Failed'
  }
}

function removeSenderIdentity(idx) {
  const i = Number(idx)
  if (!Number.isFinite(i)) return
  form.sender_identities.splice(i, 1)
}

async function toggleSenderEnabled(idx) {
  const row = form.sender_identities[idx]
  if (!row) return
  const newEnabled = row.enabled === false
  try {
    await apiFetch(`/signatures/sender-identity/${encodeURIComponent(row.email.trim().toLowerCase())}`, {
      method: 'PATCH',
      body: { enabled: newEnabled },
    })
    row.enabled = newEnabled
  } catch {
    // ignore — row stays at current state
  }
}

function formatApiError(e) {
  const msg = e?.message ? String(e.message) : ''
  const detail = e?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string' && first.trim()) return first
    if (first && typeof first === 'object') {
      const loc = Array.isArray(first.loc) ? first.loc.join('.') : ''
      const m = typeof first.msg === 'string' ? first.msg : ''
      const t = typeof first.type === 'string' ? first.type : ''
      return [loc, m, t].filter(Boolean).join(' - ') || msg || 'Failed'
    }
  }
  return msg || 'Failed'
}

async function reload() {
  error.value = ''
  try {
    const boot = await apiFetch('/settings/email/bootstrap')

    try {
      const integrationsResp = boot?.integrations || {}
      const map = {}
      for (const row of (integrationsResp?.integrations || [])) {
        const provider = String(row?.provider || '').toLowerCase()
        if (!provider) continue
        map[provider] = Boolean(row?.configured)
      }
      configuredProviders.value = map
    } catch {
      configuredProviders.value = {}
    }

    try {
      gmailConnectedUsers.value = Array.isArray(boot?.gmail_connected_users)
        ? boot.gmail_connected_users.map((u) => ({
            user_id: String(u?.user_id || ''),
            email: String(u?.email || ''),
            display_name: u?.display_name ? String(u.display_name) : null,
          })).filter((u) => u.user_id && u.email)
        : []
    } catch {
      gmailConnectedUsers.value = []
    }

    try {
      const usersResp = await apiFetch('/settings/users')
      tenantUsers.value = Array.isArray(usersResp?.users)
        ? usersResp.users.filter((u) => !u.deleted_at)
        : []
    } catch {
      tenantUsers.value = []
    }

    const s = boot?.email || {}
    form.enabled = Boolean(s.enabled)
    form.mode = s.mode || 'development'
    form.provider = s.provider || 'gmail'
    form.require_approval = s.require_approval !== false
    form.sender_identities = Array.isArray(s.sender_identities) ? s.sender_identities : []
    form.unsubscribe_text = s.unsubscribe_text || ''
    form.unsubscribe_link_label = s.unsubscribe_link_label || ''
    form.development_to_override = s.development_to_override || ''
    form.slack_channel_production = s.slack_channel_production || ''
    form.slack_channel_development = s.slack_channel_development || ''
    form.sf_writeback_production = s.sf_writeback_production || { enabled: false }
    form.default_us_timezone = s.default_us_timezone || ''
    form.default_send_timezone = s.default_send_timezone || ''
    form.timezone_aware_sending = Boolean(s.timezone_aware_sending)

    baselineSnapshot.value = JSON.stringify(_normalizedSnapshotFromForm())

    loadSlackChannels().catch(() => {})
  } catch (e) {
    error.value = e?.message || 'Failed'
  }
}

async function save() {
  if (unsubscribeTextError.value) return
  saving.value = true
  error.value = ''
  showSavedIndicator.value = false
  if (_savedTimer) { clearTimeout(_savedTimer); _savedTimer = null }
  try {
    // Salesforce writeback now lives in the integration drawer (Salesforce → Export). Echo the latest
    // stored value here so this page's full-replace PUT can never clobber a change made there.
    let sfProd = form.sf_writeback_production
    try {
      const fresh = await apiFetch('/settings/email')
      if (fresh?.sf_writeback_production) sfProd = fresh.sf_writeback_production
    } catch { /* fall back to the loaded form copy */ }
    await apiFetch('/settings/email', {
      method: 'PUT',
      body: {
        enabled: Boolean(form.enabled),
        mode: String(form.mode || 'development'),
        provider: String(form.provider || 'gmail'),
        require_approval: Boolean(form.require_approval),
        sender_identities: Array.isArray(form.sender_identities) ? form.sender_identities.map((r) => ({
          email: String(r?.email || '').trim(),
          full_name: String(r?.full_name || '').trim() || null,
          enabled: r?.enabled !== false,
          provider: String(r?.provider || 'gmail'),
          user_id: r?.user_id ? String(r.user_id) : null,
          slack_member_id: r?.slack_member_id ? String(r.slack_member_id).trim() || null : null,
        })).filter((r) => r.email) : [],
        unsubscribe_text: String(form.unsubscribe_text || '').trim() || null,
        unsubscribe_link_label: String(form.unsubscribe_link_label || '').trim() || null,
        development_to_override: String(form.development_to_override || ''),
        slack_channel_production: String(form.slack_channel_production || '').trim() || null,
        slack_channel_development: String(form.slack_channel_development || '').trim() || null,
        sf_writeback_production: sfProd,
        default_us_timezone: String(form.default_us_timezone || '').trim() || null,
        default_send_timezone: String(form.default_send_timezone || '').trim() || null,
        timezone_aware_sending: Boolean(form.timezone_aware_sending),
      },
    })
    baselineSnapshot.value = JSON.stringify(_normalizedSnapshotFromForm())
    showSavedIndicator.value = true
    if (_savedTimer) clearTimeout(_savedTimer)
    _savedTimer = setTimeout(() => { showSavedIndicator.value = false }, 2000)
  } catch (e) {
    error.value = e?.message || 'Failed'
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })
  await reload()
  if (activeTab.value === 'sending') {
    reloadSending()
    loadSendingSelects()
  }
})

onBeforeUnmount(() => {
  clearScope()
})

watch(canEnableSending, (canEnable) => {
  if (!canEnable && form.enabled) {
    form.enabled = false
  }
})

let _autoSaveTimer = null

watch(
  () => JSON.stringify(_normalizedSnapshotFromForm()),
  (cur) => {
    if (!baselineSnapshot.value) return
    if (cur === baselineSnapshot.value) return
    if (_autoSaveTimer) clearTimeout(_autoSaveTimer)
    _autoSaveTimer = setTimeout(() => {
      _autoSaveTimer = null
      save()
    }, 800)
  },
)

// ══ SENDING TAB STATE ════════════════════════════════════════════

const PAGE_SIZE = 50

// Filter state (sf_ prefix = "sending filters")
const sf_statusGroup = ref('all')
const sf_dateRange = ref('all')
const sf_search = ref('')
const sf_accountId = ref(null)
const sf_agentId = ref(null)
const sf_sequenceId = ref(null)
const sf_sendingApproach = ref('')
const sf_selectedDay = ref(null) // 'YYYY-MM-DD' or null

// SearchSelect option lists
const sf_agents = ref([])
const sf_sequences = ref([])
const sf_distinctStatuses = ref([])

const STATUS_LABELS = {
  pending_approval: 'Pending Approval',
  scheduled_for_sending: 'Scheduled for Sending',
  queued_for_sending: 'Queued for Sending',
  sending: 'Sending',
  sent: 'Sent',
  send_failed: 'Send Failed',
  bounced: 'Bounced',
  rejected: 'Rejected',
  deleted: 'Deleted',
}

const sf_statusOptions = computed(() => [
  { value: 'all', label: 'All statuses' },
  ...sf_distinctStatuses.value.map((s) => ({ value: s, label: STATUS_LABELS[s] || s })),
])

const sf_sendingApproachOptions = [
  { value: '', label: 'All approaches' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'immediate', label: 'Immediate' },
]

async function searchAccountsById(query) {
  const qs = new URLSearchParams({ q: query, limit: '20' })
  const resp = await apiFetch(`/accounts/paginated?${qs}`)
  const accts = Array.isArray(resp?.items) ? resp.items : []
  return accts.map((a) => ({
    value: String(a.id || ''),
    label: String(a.name || a.domain || '').trim() || String(a.domain || ''),
    domain: String(a.domain || ''),
  }))
}

const sf_agentOptions = computed(() =>
  sf_agents.value.map((a) => ({
    value: a.id,
    label: a.name || String(a.id),
    iconName: a.icon_name || undefined,
    iconStyle: a.icon_style || undefined,
    iconColor: a.icon_color || undefined,
  })),
)

const sf_sequenceOptions = computed(() =>
  sf_sequences.value.map((s) => ({
    value: s.id,
    label: s.name || String(s.id),
  })),
)

// Data state
const sf_rows = ref([])
const sf_total = ref(0)
const sf_nextOffset = ref(null)
const sf_loading = ref(false)

// Summary counts (null = not yet loaded)
const sf_summary = reactive({
  sent: null,
  scheduled: null,
  pending: null,
  failed: null,
})

// Status group → status list mapping.
// Card groups use named keys; raw individual statuses map to single-item arrays.
const STATUS_GROUPS = {
  all: [],
  // Card group shortcuts
  sent: ['sent'],
  scheduled: ['scheduled_for_sending', 'queued_for_sending', 'sending'],
  pending_approval: ['pending_approval'],
  failed: ['send_failed'],
  // Raw individual statuses (used by the status dropdown)
  scheduled_for_sending: ['scheduled_for_sending'],
  queued_for_sending: ['queued_for_sending'],
  sending: ['sending'],
  send_failed: ['send_failed'],
  rejected: ['rejected'],
  deleted: ['deleted'],
}

const STATUS_COLORS = {
  pending_approval: { color: '#d97706', bg: 'rgba(217,119,6,0.1)' },
  scheduled_for_sending: { color: '#6366f1', bg: 'rgba(99,102,241,0.1)' },
  queued_for_sending: { color: '#6366f1', bg: 'rgba(99,102,241,0.1)' },
  sending: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  sent: { color: '#059669', bg: 'rgba(5,150,105,0.1)' },
  send_failed: { color: '#dc2626', bg: 'rgba(220,38,38,0.1)' },
  rejected: { color: '#6b7280', bg: 'rgba(107,114,128,0.1)' },
  deleted: { color: '#6b7280', bg: 'rgba(107,114,128,0.1)' },
}

function statusPillStyle(s) {
  const cfg = STATUS_COLORS[s]
  if (!cfg) return {}
  return { color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}33` }
}

// "scheduled" = went / will go through the timezone-aware scheduler.
// For SENT emails: authoritative indicator is sent_timezone_source (null or "immediate" = Kafka path).
// For PENDING emails: scheduled_for_sending status or a resolved send_timezone.
function sendingPath(row) {
  if (row.status === 'sent') {
    return (row.sent_timezone_source == null || row.sent_timezone_source === 'immediate') ? 'immediate' : 'scheduled'
  }
  if (row.status === 'scheduled_for_sending' || row.send_timezone) return 'scheduled'
  return 'immediate'
}

// Effective timezone: for sent emails use sent_timezone; for pending use send_timezone.
function effectiveTz(row) {
  return row.sent_timezone || row.send_timezone || null
}

// Source label for the timezone used.
function effectiveTzSource(row) {
  const src = row.sent_timezone_source
  if (!src || src === 'none') return null
  if (src === 'contact') return 'contact'
  if (src === 'tenant_default') return 'tenant default'
  return src
}

// Pre-send override (dev mode, not yet sent) OR post-send actual_to (after send).
function recipientOverride(row) {
  if (row.actual_to && row.actual_to !== row.to) {
    return { address: row.actual_to, sent: true }
  }
  const devOverride = row.email_settings?.development_to_override
  if (devOverride && row.email_settings?.mode === 'development') {
    return { address: devOverride, sent: false }
  }
  return null
}

function formatDateTime(dt) {
  if (!dt) return '—'
  const d = new Date(dt)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

// Returns milliseconds until next Mon-Fri 9am-5pm window in the given IANA timezone.
// Returns 0 if currently inside that window. Returns null on error.
function msUntilNextBizWindow(timezone) {
  if (!timezone) return null
  try {
    const now = new Date()
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: 'numeric',
      hour12: false,
      minute: 'numeric',
    })
    const parts = fmt.formatToParts(now)
    const weekday = parts.find((p) => p.type === 'weekday')?.value   // 'Mon'…'Sun'
    const hour    = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0')
    const minute  = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0')

    const DAY = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
    const dayNum      = DAY[weekday] ?? -1
    const minuteOfDay = hour * 60 + minute
    const BIZ_START   = 9 * 60   // 540
    const BIZ_END     = 17 * 60  // 1020

    let minutesAway = 0

    if (dayNum >= 1 && dayNum <= 5) {
      if (minuteOfDay >= BIZ_START && minuteOfDay < BIZ_END) {
        return 0  // currently in window
      } else if (minuteOfDay < BIZ_START) {
        // Before 9am on a weekday
        minutesAway = BIZ_START - minuteOfDay
      } else {
        // After 5pm on a weekday — next window is next weekday 9am
        const daysForward = dayNum === 5 ? 3 : 1   // Fri → Mon (+3 days), else +1 day
        minutesAway = (24 * 60 - minuteOfDay) + BIZ_START + (daysForward - 1) * 24 * 60
      }
    } else {
      // Weekend — jump to Monday 9am
      const daysForward = dayNum === 6 ? 2 : 1    // Sat → +2, Sun → +1
      minutesAway = (24 * 60 - minuteOfDay) + BIZ_START + (daysForward - 1) * 24 * 60
    }

    return minutesAway * 60 * 1000
  } catch {
    return null
  }
}

function bizWindowEstimate(row) {
  const tz = row.send_timezone  // contact timezone, present for pending scheduled emails
  if (!tz) return null
  const ms = msUntilNextBizWindow(tz)
  if (ms === null) return null
  if (ms === 0) return 'sending now'
  const totalMins = Math.floor(ms / 60_000)
  const hours = Math.floor(totalMins / 60)
  const days  = Math.floor(hours / 24)
  const remH  = hours % 24
  if (days === 0) return hours === 0 ? 'in under an hour' : `in ${hours}h`
  if (remH === 0) return `in ${days}d`
  return `in ${days}d ${remH}h`
}

function getDateRangeParams(range) {
  const now = new Date()
  if (range === 'today') {
    const start = new Date(now)
    start.setHours(0, 0, 0, 0)
    return { created_after: start.toISOString() }
  }
  if (range === '7d') {
    const start = new Date(now)
    start.setDate(start.getDate() - 7)
    return { created_after: start.toISOString() }
  }
  if (range === '30d') {
    const start = new Date(now)
    start.setDate(start.getDate() - 30)
    return { created_after: start.toISOString() }
  }
  if (range === '90d') {
    const start = new Date(now)
    start.setDate(start.getDate() - 90)
    return { created_after: start.toISOString() }
  }
  return {}
}

function buildReachoutsUrl({
  statuses = [],
  search = '',
  dateRange = 'all',
  limit = PAGE_SIZE,
  offset = 0,
  includeDeleted = false,
  accountId = null,
  agentId = null,
  sequenceId = null,
  sendingApproach = '',
  dayFilter = null,
} = {}) {
  const params = new URLSearchParams()
  params.append('types', 'reachout_email')
  for (const s of statuses) params.append('statuses', s)
  const trimmed = String(search || '').trim()
  if (trimmed) params.set('search', trimmed)
  if (sendingApproach) params.set('sending_approach', sendingApproach)
  if (dayFilter) {
    // Filter to a single calendar day (local midnight → next midnight)
    const [y, m, d] = dayFilter.split('-').map(Number)
    const start = new Date(y, m - 1, d, 0, 0, 0, 0)
    const end = new Date(y, m - 1, d, 23, 59, 59, 999)
    params.set('created_after', start.toISOString())
    params.set('created_before', end.toISOString())
  } else {
    const dr = getDateRangeParams(dateRange)
    if (dr.created_after) params.set('created_after', dr.created_after)
  }
  if (accountId) params.set('account_id', String(accountId))
  if (agentId) params.set('agent_id', String(agentId))
  if (sequenceId) params.set('sequence_id', String(sequenceId))
  params.set('limit', String(limit))
  params.set('offset', String(offset))
  if (includeDeleted) params.set('include_deleted', 'true')
  return `/reachouts?${params.toString()}`
}

async function fetchSendingCount({ statuses, dateRange }) {
  try {
    const url = buildReachoutsUrl({
      statuses,
      dateRange,
      limit: 1,
      offset: 0,
      includeDeleted: statuses.includes('deleted'),
      accountId: sf_accountId.value,
      agentId: sf_agentId.value,
      sequenceId: sf_sequenceId.value,
    })
    const resp = await apiFetch(url)
    return typeof resp?.total === 'number' ? resp.total : 0
  } catch {
    return 0
  }
}

async function loadSendingSummary() {
  const dateRange = sf_dateRange.value
  sf_summary.sent = null
  sf_summary.scheduled = null
  sf_summary.pending = null
  sf_summary.failed = null

  const [sent, scheduled, pending, failed] = await Promise.all([
    fetchSendingCount({ statuses: STATUS_GROUPS.sent, dateRange }),
    fetchSendingCount({ statuses: STATUS_GROUPS.scheduled, dateRange }),
    fetchSendingCount({ statuses: STATUS_GROUPS.pending_approval, dateRange }),
    fetchSendingCount({ statuses: STATUS_GROUPS.failed, dateRange }),
  ])

  sf_summary.sent = sent
  sf_summary.scheduled = scheduled
  sf_summary.pending = pending
  sf_summary.failed = failed
}

async function loadSendingRows(offset = 0) {
  sf_loading.value = true
  try {
    const statuses = STATUS_GROUPS[sf_statusGroup.value] ?? []
    const includeDeleted = sf_statusGroup.value === 'deleted'
    const url = buildReachoutsUrl({
      statuses,
      search: sf_search.value,
      dateRange: sf_dateRange.value,
      limit: PAGE_SIZE,
      offset,
      includeDeleted,
      accountId: sf_accountId.value,
      agentId: sf_agentId.value,
      sequenceId: sf_sequenceId.value,
      sendingApproach: sf_sendingApproach.value,
      dayFilter: sf_selectedDay.value,
    })
    const resp = await apiFetch(url)
    const rows = Array.isArray(resp?.reachouts) ? resp.reachouts : []
    if (offset === 0) {
      sf_rows.value = rows
    } else {
      sf_rows.value = [...sf_rows.value, ...rows]
    }
    sf_total.value = typeof resp?.total === 'number' ? resp.total : sf_rows.value.length
    sf_nextOffset.value = resp?.next_offset ?? null
  } catch (e) {
    // non-fatal; table just stays empty
  } finally {
    sf_loading.value = false
  }
}

async function reloadSending() {
  await Promise.all([
    loadSendingRows(0),
    loadSendingSummary(),
    loadChartData(),
  ])
}

async function loadMoreSending() {
  if (sf_nextOffset.value === null || sf_loading.value) return
  await loadSendingRows(sf_nextOffset.value)
}

function setSendingStatusGroup(group) {
  // Toggle: clicking an already-active card resets to 'all'
  sf_statusGroup.value = sf_statusGroup.value === group ? 'all' : group
  loadSendingRows(0)
}

function onSendingFilterChange() {
  loadSendingRows(0)
  loadSendingSummary()
  loadChartData()
}

function onDateRangeChange() {
  // Date range doesn't affect the chart (always 30 days) but does affect the table
  loadSendingRows(0)
  loadSendingSummary()
}

let _searchDebounce = null
function onSendingSearchInput() {
  if (_searchDebounce) clearTimeout(_searchDebounce)
  _searchDebounce = setTimeout(() => {
    _searchDebounce = null
    loadSendingRows(0)
    loadChartData()
  }, 350)
}

// ── Bar chart ─────────────────────────────────────────────────────

const CHART_BAR_HEIGHT = 52 // px for the bar fill area

// 30-day chart data; each entry: { date, sent, scheduled, pending, failed, other }
const sf_chartData = ref([])

const sf_chartMax = computed(() => {
  if (!sf_chartData.value.length) return 1
  return Math.max(
    1,
    ...sf_chartData.value.map((d) => d.sent + d.scheduled + d.pending + d.failed + d.other),
  )
})

// Returns stacked segments for a bar (bottom-to-top order: sent, scheduled, pending, failed, other)
function barParts(day) {
  const total = day.sent + day.scheduled + day.pending + day.failed + day.other
  if (!total) return []
  const barH = (total / sf_chartMax.value) * CHART_BAR_HEIGHT
  const segs = [
    { color: '#059669', count: day.sent },      // green  — sent
    { color: '#6366f1', count: day.scheduled }, // purple — scheduled
    { color: '#d97706', count: day.pending },   // amber  — pending_approval
    { color: '#dc2626', count: day.failed },    // red    — send_failed
    { color: '#9ca3af', count: day.other },     // gray   — other
  ]
  return segs
    .filter((s) => s.count > 0)
    .map((s) => ({ color: s.color, h: (s.count / total) * barH }))
}

function chartLabel(dateStr) {
  // Show "M/D" — at narrow width only the day number matters
  const parts = dateStr.split('-')
  return parseInt(parts[1], 10) + '/' + parseInt(parts[2], 10)
}

function onChartBarClick(date) {
  sf_selectedDay.value = sf_selectedDay.value === date ? null : date
  loadSendingRows(0)
}

async function loadChartData() {
  try {
    // Always fetch last 30 days, all statuses, with current non-date/non-status filters
    const start = new Date()
    start.setDate(start.getDate() - 29)
    start.setHours(0, 0, 0, 0)
    const params = new URLSearchParams()
    params.append('types', 'reachout_email')
    params.set('created_after', start.toISOString())
    params.set('limit', '500')
    params.set('offset', '0')
    params.set('include_deleted', 'true')
    if (sf_accountId.value) params.set('account_id', String(sf_accountId.value))
    if (sf_agentId.value) params.set('agent_id', String(sf_agentId.value))
    if (sf_sequenceId.value) params.set('sequence_id', String(sf_sequenceId.value))
    const trimmed = sf_search.value.trim()
    if (trimmed) params.set('search', trimmed)

    const resp = await apiFetch(`/reachouts?${params.toString()}`)
    const rows = Array.isArray(resp?.reachouts) ? resp.reachouts : []

    // Build a map of the last 30 days
    const days = []
    for (let i = 29; i >= 0; i--) {
      const d = new Date()
      d.setDate(d.getDate() - i)
      const key = d.toISOString().slice(0, 10)
      days.push({ date: key, sent: 0, scheduled: 0, pending: 0, failed: 0, other: 0 })
    }
    const dayMap = Object.fromEntries(days.map((d) => [d.date, d]))

    for (const row of rows) {
      const dateKey = (row.created_at || '').slice(0, 10)
      const entry = dayMap[dateKey]
      if (!entry) continue
      const s = row.status
      if (s === 'sent') entry.sent++
      else if (s === 'scheduled_for_sending' || s === 'queued_for_sending' || s === 'sending') entry.scheduled++
      else if (s === 'pending_approval') entry.pending++
      else if (s === 'send_failed') entry.failed++
      else entry.other++
    }

    sf_chartData.value = days
  } catch {
    // non-fatal; chart just stays empty
  }
}
</script>

<style scoped>
/* ── Tab nav ──────────────────────────────────────────────────── */
.tabNav {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 16px;
}

.tabBtn {
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  background: none;
  color: var(--muted);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  border-radius: 0;
  transition: color 0.15s, border-color 0.15s;
}

.tabBtn:hover {
  color: var(--text);
}

.tabBtn.active {
  color: var(--accent);
  border-bottom-color: var(--accent);
}

/* ── Shared card / form styles (same as before) ───────────────── */
.card {
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 12px;
  padding: 14px;
  max-width: 900px;
}

.lead {
  color: var(--muted);
  font-size: 13px;
  margin-bottom: 12px;
}

.hint {
  color: var(--muted);
  font-size: 12px;
  margin: 10px 0 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

@media (max-width: 900px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

label {
  font-size: 12px;
  font-weight: 700;
  color: var(--table-header-text);
}

.input {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 14px;
  line-height: 1.25;
  height: 40px;
}

.textarea {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  font-size: 14px;
  line-height: 1.35;
}

.error {
  margin-bottom: 10px;
  color: var(--danger);
}

.autoSaveIndicator {
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
}

.autoSaveIndicator.saved {
  color: var(--success);
}

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
  margin-left: 8px;
}

.iconBtn svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

.iconBtn:hover {
  border-color: rgba(37, 99, 235, 0.35);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.10);
}

.iconBtn.danger {
  color: var(--danger);
  background: rgba(220, 38, 38, 0.06);
  border-color: rgba(220, 38, 38, 0.18);
}

.iconBtn.danger:hover {
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.12);
}

.iconBtn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}

.section {
  border-top: 1px solid var(--border);
  padding-top: 14px;
  margin-top: 14px;
}

.section-title {
  font-size: 11px;
  font-weight: 800;
  color: var(--table-header-text);
  letter-spacing: 0.06em;
  margin-bottom: 10px;
  text-transform: uppercase;
}

.send-toggle {
  border: 1px solid rgba(245, 158, 11, 0.5);
  background: rgba(245, 158, 11, 0.08);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
}

.approvalCard {
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 14px;
  background: var(--surface);
}

.providerBadges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

.providerBadge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
}

.providerPill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid var(--border);
  white-space: nowrap;
}
.statusPill {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  transition: opacity 120ms ease;
}
.statusPill:hover { opacity: 0.75; }
.statusEnabled {
  color: var(--success, #059669);
  background: hsl(160, 60%, 95%);
}
.statusDisabled {
  color: var(--muted);
  background: var(--bg-subtle, #f0f0f0);
}

.senderTableWrap {
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 12px;
  margin-top: 10px;
}

.senderTable {
  width: 100%;
  border-collapse: collapse;
  min-width: 820px;
}

.senderTable th,
.senderTable td {
  padding: 10px;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.senderTable th {
  text-align: left;
  font-size: 12px;
  font-weight: 800;
  color: var(--table-header-text);
  background: var(--table-header-bg);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.muted {
  color: var(--muted);
}

/* ── Sending tab ──────────────────────────────────────────────── */
.sendingTab {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sendingFilters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.filterSelect {
  width: auto;
  min-width: 160px;
  flex-shrink: 0;
}

.filterSearch {
  flex: 1;
  min-width: 180px;
}

/* Summary cards */
.summaryCards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 700px) {
  .summaryCards {
    grid-template-columns: repeat(2, 1fr);
  }
}

.summaryCard {
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 14px 16px;
  background: var(--surface);
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
  user-select: none;
}

.summaryCard:hover {
  border-color: var(--accent);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.summaryCard.active {
  border-color: var(--accent);
  background: rgba(37, 99, 235, 0.06);
}

.summaryCard.danger .summaryCardValue {
  color: var(--danger);
}

.summaryCard.danger.active {
  border-color: var(--danger);
  background: rgba(220, 38, 38, 0.06);
}

.summaryCard.danger:hover {
  border-color: var(--danger);
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.1);
}

.summaryCardValue {
  font-size: 26px;
  font-weight: 800;
  color: var(--text);
  line-height: 1.1;
}

.summaryCardLabel {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 4px;
}

/* Email table */
.emailTableWrap {
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 12px;
}

.emailTable {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
  font-size: 13px;
}

.emailTable th {
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 800;
  color: var(--table-header-text);
  background: var(--table-header-bg);
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
}

.emailTable td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.emailTable tbody tr:last-child td {
  border-bottom: none;
}

.emailTable tbody tr:hover td {
  background: var(--table-hover, rgba(0,0,0,0.02));
}

.cellMain {
  font-size: 13px;
  line-height: 1.3;
}

.cellSub {
  font-size: 11px;
  color: var(--muted);
  margin-top: 2px;
  line-height: 1.3;
}

.cellTruncate {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.statusPill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

/* Constrain badge text in table cells */
.emailTable .badge {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
}

/* Path badges */
.pathBadge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 5px;
}

.pathBadge.scheduled {
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
  border: 1px solid rgba(99, 102, 241, 0.25);
}

.pathBadge.immediate {
  background: rgba(107, 114, 128, 0.08);
  color: #6b7280;
  border: 1px solid rgba(107, 114, 128, 0.22);
}

/* Sending detail rows */
.sendingDetail {
  font-size: 11px;
  color: var(--text);
  margin-top: 3px;
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.sendingDetailMuted {
  font-size: 11px;
  color: var(--muted);
  font-style: italic;
  margin-top: 3px;
}

.sendingDetailLabel {
  font-size: 10px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  min-width: 36px;
  flex-shrink: 0;
}

.tzSourceBadge {
  display: inline-block;
  padding: 0px 5px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: rgba(107, 114, 128, 0.1);
  color: var(--muted);
  border: 1px solid rgba(107, 114, 128, 0.2);
}

/* Dev override badge */
.devBadge {
  display: inline-block;
  padding: 0 5px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.25);
  margin-right: 3px;
}

.devBadgePre {
  color: var(--muted);
  font-size: 10px;
  margin-left: 2px;
}

/* Table footer */
.tableFooter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 0;
}

/* ── Bar chart ──────────────────────────────────────────────────── */
.chartSection {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chartBars {
  display: flex;
  align-items: stretch;
  gap: 2px;
  height: 82px; /* bar area (52px) + label (14px) + vertical padding (16px) */
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 8px 4px;
  background: var(--surface);
  overflow: hidden;
}

.chartBarCol {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  cursor: pointer;
  border-radius: 3px;
  transition: opacity 0.12s;
  padding: 0 1px;
}

.chartBarCol:hover {
  opacity: 0.75;
}

.chartBarCol.active {
  background: rgba(37, 99, 235, 0.07);
  border-radius: 3px;
  outline: 1.5px solid var(--accent);
  outline-offset: 0px;
}

.chartBarArea {
  display: flex;
  flex-direction: column-reverse; /* bottom segment first */
  width: 100%;
  border-radius: 2px 2px 0 0;
  overflow: hidden;
  min-height: 0;
}

.chartSeg {
  width: 100%;
  flex-shrink: 0;
}

.chartBarLabel {
  font-size: 8px;
  color: var(--muted);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  padding-top: 3px;
  line-height: 1;
  height: 12px;
}

.chartDayFilter {
  font-size: 12px;
  color: var(--muted);
  padding: 2px 0;
}

.btnLink {
  background: none;
  border: none;
  color: var(--accent);
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  margin-left: 6px;
  text-decoration: underline;
}

.btnLink:hover {
  opacity: 0.75;
}
</style>
